import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "./AdminLayout";
import ThemeModal from "./ThemeModal";
import { adminPost, getAdminSession } from "./adminApi";
import "./admin.css";

const displayName = (t) => t.newthemeName || t.themeName || t.themename;

// Port of themes.php: list themes, select one, create / edit / delete.
export default function AdminThemes() {
  const navigate = useNavigate();
  const session = getAdminSession();
  const [themes, setThemes] = useState(null);
  const [modal, setModal] = useState(null); // { mode: "create" } | { mode: "edit", theme }

  const load = useCallback(async () => {
    try {
      const res = await adminPost("fetchAllThemes", {}, session);
      setThemes(res.data || []);
    } catch (err) {
      Swal.fire({ icon: "error", title: err.message });
      setThemes([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!session) {
      navigate("/admin", { replace: true });
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!session) return null;

  const selectTheme = async (theme) => {
    if (theme.selected === "true") return;
    try {
      await adminPost("selectTheme", { themeName: theme.themename }, session);
      await Swal.fire({ icon: "success", title: "Theme Selected", timer: 1200, showConfirmButton: false });
      load();
    } catch (err) {
      Swal.fire({ icon: "error", title: err.message });
    }
  };

  const deleteTheme = async (e, theme) => {
    e.stopPropagation();
    const result = await Swal.fire({
      title: "Are you sure you want to delete theme",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      await adminPost("deleteTheme", { themeName: theme.themename }, session);
      load();
    } catch (err) {
      Swal.fire({ icon: "error", title: err.message });
    }
  };

  const selected = (themes || []).filter((t) => t.selected === "true");

  return (
    <AdminLayout session={session} title="Themes">
      <div className="adm-themes">
        <div className="adm-card adm-selected">
          {selected.length === 0 && themes && <p>No theme selected.</p>}
          {selected.map((t) => (
            <div className="adm-selected-theme" key={t.themename}>
              <img src={t.themeImageUrl} alt={displayName(t)} />
              <div>
                <h4>{displayName(t)}</h4>
                <h5>{t.themeDescription}</h5>
              </div>
            </div>
          ))}
        </div>

        <div className="adm-card">
          <div className="adm-card-head">
            <h5>Select Themes</h5>
            <button type="button" className="adm-btn" onClick={() => setModal({ mode: "create" })}>
              + Create Theme
            </button>
          </div>
          {themes === null && <p>Loading...</p>}
          <div className="adm-theme-grid">
            {(themes || []).map((t) => (
              <div
                key={t.themename}
                className={`adm-theme-card${t.selected === "true" ? " is-selected" : ""}`}
                onClick={() => selectTheme(t)}
                title={displayName(t)}
              >
                <img src={t.themeImageUrl} alt={displayName(t)} />
                {t.selected === "true" && <div className="adm-theme-check" title="Selected"><i className="fa-solid fa-check" /></div>}
                <div className="adm-theme-actions">
                  <button
                    type="button"
                    title="Edit Image"
                    onClick={(e) => { e.stopPropagation(); setModal({ mode: "edit", theme: t }); }}
                  ><i className="fa-solid fa-image" /></button>
                  <button
                    type="button"
                    title="Settings"
                    onClick={(e) => { e.stopPropagation(); navigate(`/admin/rules/${encodeURIComponent(t.themename)}`); }}
                  ><i className="fa-solid fa-gear" /></button>
                  <button type="button" title="Delete" onClick={(e) => deleteTheme(e, t)}><i className="fa-solid fa-trash" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {modal && (
        <ThemeModal
          mode={modal.mode}
          theme={modal.theme}
          session={session}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </AdminLayout>
  );
}
